import { AgGridAngular } from "@ag-grid-community/angular";
import { ColDef, GridOptions, ICellRendererParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal, viewChild } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Currency, DspAdgroupStateEnum, DspDeliveryStatus, DspStats, Strategy } from "@front/m19-api-client";
import {
  AccountSelectionService,
  DspService,
  MetricsSelectorPage,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { ActionButton, ActionButtonsComponent, ITab, ITabsComponent, ModalOptions, ModalService } from "@front/m19-ui";
import { isEmptyDspStats, sumDspStats } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import { restoreDefaultColumns } from "@m19-board/grid-config/grid-config";
import { DSP_COST, DSP_SALES, DspStrategyMetrics } from "@m19-board/models/DspMetrics";
import { ChartModalComponent, ChartModalInputData } from "@m19-board/shared/chart-modal/chart-modal.component";
import { combineLatest, of, switchMap } from "rxjs";
import { DspCreativeStats, DspGridColumnsService, dspGridOptions } from "./dsp-grid-columns.service";
import {
  DspStatsCreativeDetailsModalComponent,
  DspStatsCreativeDetailsModalData,
} from "./dsp-stats-creative-details-modal.component";
import {
  DspStatsLineItemDetailsModalComponent,
  DspStatsLineItemDetailsModalData,
} from "./dsp-stats-line-item-details-modal.component";
import { AmcStrategyAnalysesComponent } from "@m19-board/amc/amc-strategy-analyses/amc-strategy-analyses.component";
import { AmcStrategyAnalysisService } from "@m19-board/amc/amc-strategy-analysis-service/amc-strategy-analysis.service";

enum DspStrategyStatsSection {
  LINE_ITEMS = 0,
  CREATIVES = 1,
  INVENTORY = 2,
  ADVANCED_ANALYSIS = 3,
}

type DspLineItemStats = DspStats & {
  name?: string;
  state?: DspAdgroupStateEnum;
  deliveryStatus?: DspDeliveryStatus;
  stats?: DspStats[];
  previousData?: DspStats[];
  previousTotalData?: DspStats;
};

export type DspInventoryStats = DspStats & {
  stats?: DspStats[];
  previousData?: DspStats[];
  previousTotalData?: DspStats;
};

@Component({
  selector: "app-dsp-strategy-stats-grid",
  template: `
    <div class="mb-6">
      <ITabs [tabs]="tabs()" [(activeTab)]="activeTab" />
    </div>
    @switch (activeTab()) {
      @case (Section.LINE_ITEMS) {
        <div class="ag-theme-quartz h-[30rem]">
          <ag-grid-angular
            class="h-full"
            #lineItemGrid
            [gridOptions]="lineItemGridOptions()"
            [columnDefs]="lineItemGridColumnDefs()"
            [rowData]="lineItemRowData()"
          />
        </div>
      }
      @case (Section.CREATIVES) {
        <div class="ag-theme-quartz h-[30rem]">
          <ag-grid-angular
            class="h-full"
            #creativeGrid
            [gridOptions]="creativeGridOptions()"
            [columnDefs]="creativeGridColumnDefs()"
            [rowData]="creativeRowData()"
          />
        </div>
      }
      @case (Section.INVENTORY) {
        <div class="ag-theme-quartz h-[30rem]">
          <ag-grid-angular
            class="h-full"
            #inventoryGrid
            [gridOptions]="inventoryGridOptions()"
            [columnDefs]="inventoryGridColumnDefs()"
            [rowData]="inventoryRowData()"
          />
        </div>
      }
      @case (Section.ADVANCED_ANALYSIS) {
        <app-amc-strategy-analyses [strategyId]="strategy().strategyId" />
      }
    }
  `,
  imports: [AgGridAngular, ITabsComponent, AmcStrategyAnalysesComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspStrategyStatsGridComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly dspColumns = inject(DspGridColumnsService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly statsService = inject(StatsService);
  private readonly dspService = inject(DspService);
  private readonly amcStrategyAnalysisService = inject(AmcStrategyAnalysisService);
  private readonly modalService = inject(ModalService);

  private readonly lineItemGrid = viewChild.required<AgGridAngular>("lineItemGrid");
  private readonly creativeGrid = viewChild.required<AgGridAngular>("creativeGrid");
  private readonly inventoryGrid = viewChild.required<AgGridAngular>("inventoryGrid");

  readonly strategy = input.required<Strategy>();
  readonly strategyMetrics = input.required<DspStrategyMetrics>();
  readonly currency = input.required<Currency>();
  readonly locale = input.required<string>();

  readonly tabs = computed<ITab<DspStrategyStatsSection>[]>(() => [
    {
      label: this.translocoService.translate("dsp-strategy-stats-grid.line_items"),
      value: DspStrategyStatsSection.LINE_ITEMS,
    },
    {
      label: this.translocoService.translate("dsp-strategy-stats-grid.creatives"),
      value: DspStrategyStatsSection.CREATIVES,
    },
    {
      label: this.translocoService.translate("dsp-strategy-stats-grid.inventory"),
      value: DspStrategyStatsSection.INVENTORY,
    },
    {
      label: this.translocoService.translate("dsp-strategy-stats-grid.advanced_analysis"),
      value: DspStrategyStatsSection.ADVANCED_ANALYSIS,
      hidden: !this.availableAmcAnalyses(),
    },
  ]);
  readonly Section = DspStrategyStatsSection;
  readonly activeTab = signal<DspStrategyStatsSection>(DspStrategyStatsSection.LINE_ITEMS);

  private readonly dspLineItemStats = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) =>
        combineLatest([this.userSelectionService.dateRange$, this.userSelectionService.selectedCurrency$]).pipe(
          switchMap(([[start, end], currency]) =>
            this.statsService.getDspLineItemsStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              accountMarketplace.dspAdvertiserId!,
              currency,
              start,
              end,
            ),
          ),
        ),
      ),
    ),
  );
  private readonly previousDspLineItemStats = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) =>
        combineLatest([this.userSelectionService.periodComparison$, this.userSelectionService.selectedCurrency$]).pipe(
          switchMap(([periodCompararison, currency]) => {
            if (!periodCompararison || !periodCompararison.period || periodCompararison.period.length < 2) {
              return of([]);
            }
            return this.statsService.getDspLineItemsStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              accountMarketplace.dspAdvertiserId!,
              currency,
              periodCompararison.period[0],
              periodCompararison.period[1],
            );
          }),
        ),
      ),
    ),
  );
  private readonly dspStrategyLineItems = computed(() => {
    return this.filterStats(this.dspLineItemStats());
  });
  private readonly previousDspStrategyLineItemStats = computed(() => {
    return this.filterStats(this.previousDspLineItemStats());
  });

  private readonly dspInventoryStats = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) =>
        combineLatest([this.userSelectionService.dateRange$, this.userSelectionService.selectedCurrency$]).pipe(
          switchMap(([[start, end], currency]) =>
            this.statsService.getDspInventoryStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              accountMarketplace.dspAdvertiserId!,
              currency,
              start,
              end,
            ),
          ),
        ),
      ),
    ),
  );
  private readonly previousDspInventoryStats = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) =>
        combineLatest([this.userSelectionService.periodComparison$, this.userSelectionService.selectedCurrency$]).pipe(
          switchMap(([periodCompararison, currency]) => {
            if (!periodCompararison || !periodCompararison.period || periodCompararison.period.length < 2) {
              return of([]);
            }
            return this.statsService.getDspInventoryStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              accountMarketplace.dspAdvertiserId!,
              currency,
              periodCompararison.period[0],
              periodCompararison.period[1],
            );
          }),
        ),
      ),
    ),
  );
  private readonly dspStrategyInventoryStats = computed(() => {
    return this.filterStats(this.dspInventoryStats());
  });
  private readonly previousDspStrategyInventoryStats = computed(() => {
    return this.filterStats(this.previousDspInventoryStats());
  });

  private readonly previousDspLineItemStatsMap = computed(() => {
    return this.aggregatePreviousData(this.previousDspStrategyLineItemStats(), (s) => s.lineItemId);
  });
  private readonly previousDspCreativeStatsMap = computed(() => {
    return this.aggregatePreviousData(this.previousDspStrategyInventoryStats(), (s) => s.creativeId);
  });
  private readonly previousDspInventoryStatsMap = computed(() => {
    return this.aggregatePreviousData(this.previousDspStrategyInventoryStats(), (s) => s.site! + "_" + s.supplySource!);
  });

  private readonly dspCreatives = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) => this.dspService.getDspCreatives(accountMarketplace.dspAdvertiserId!)),
    ),
  );
  private readonly dspLineItems = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace) => this.dspService.getDspAdgroups(accountMarketplace.dspAdvertiserId!)),
    ),
  );

  readonly lineItemRowData = computed(() => {
    const stats = this.dspStrategyLineItems();
    const lineItems = this.dspLineItems() ?? [];
    const lineItemStatsFn = (stat: DspStats): DspLineItemStats => {
      const lineItem = lineItems.find((l) => l.adGroupId === stat.lineItemId);
      return {
        ...stat,
        name: lineItem?.name ?? "",
        state: lineItem?.state,
        deliveryStatus: lineItem?.status?.deliveryStatus,
      };
    };

    return this.buildRowData<DspLineItemStats>(
      stats,
      this.previousDspLineItemStatsMap(),
      (stat: DspStats) => stat.lineItemId!,
      lineItemStatsFn,
    );
  });

  readonly creativeRowData = computed(() => {
    const stats = this.dspStrategyLineItems();
    const creatives = this.dspCreatives() ?? [];
    const creativeStatsFn = (stat: DspStats): DspCreativeStats => {
      const creative = creatives.find((c) => c.adCreativeId === stat.creativeId);
      return {
        ...stat,
        dspCreativeName: creative?.name ?? "",
        type: creative?.adCreativeFormatProperties?.adCreativeFormatType,
        lineItemStats: [], // not used
      };
    };
    return this.buildRowData<DspCreativeStats>(
      stats,
      this.previousDspCreativeStatsMap(),
      (stat: DspStats) => stat.creativeId!,
      creativeStatsFn,
    );
  });

  readonly inventoryRowData = computed<DspInventoryStats[]>(() => {
    const stats = this.dspStrategyInventoryStats();
    const inventoryStatsFn = (stat: DspStats): DspInventoryStats => stat;
    return this.buildRowData<DspInventoryStats>(
      stats,
      this.previousDspInventoryStatsMap(),
      (stat: DspStats) => stat.site! + "_" + stat.supplySource!,
      inventoryStatsFn,
    );
  });

  private readonly lineItemGridKey = computed(
    () => "dsp_strategy_stats_line_items_" + this.strategyMetrics().storageKeySuffix,
  );
  private readonly creativeGridKey = computed(
    () => "dsp_strategy_stats_creatives_" + this.strategyMetrics().storageKeySuffix,
  );
  private readonly inventoryGridKey = computed(
    () => "dsp_strategy_stats_inventory_" + this.strategyMetrics().storageKeySuffix,
  );

  readonly lineItemGridOptions = computed(() => this.gridOptions<DspLineItemStats>(this.lineItemGridKey()));
  readonly lineItemGridColumnDefs = computed<ColDef<DspLineItemStats>[]>(() => [
    this.dspColumns.lineItemNameColumn<DspLineItemStats>(),
    this.dspColumns.lineItemStateColumn<DspLineItemStats>({ enableRowGroup: true }),
    this.dspColumns.deliveryStatusColumn<DspLineItemStats>({ enableRowGroup: true }),
    ...this.metricsColumns<DspLineItemStats>(),
    this.dspColumns.currencyColumn<DspLineItemStats>(),
    this.buildActionColumn<DspLineItemStats>(
      (data) => this.translocoService.translate("dsp-stats.line_item") + " - " + data.name,
      this.translocoService.translate("dsp-strategy-stats-grid.display_line_item_stats_chart"),
      "lineItemDetails",
    ),
  ]);

  readonly creativeGridOptions = computed(() => this.gridOptions<DspCreativeStats>(this.creativeGridKey()));
  readonly creativeGridColumnDefs = computed<ColDef<DspCreativeStats>[]>(() => [
    this.dspColumns.creativeNameColumn<DspCreativeStats>(),
    this.dspColumns.creativeTypeColumn<DspCreativeStats>({
      colId: "type",
      value: (data) => data.type,
      enableRowGroup: true,
    }),
    ...this.metricsColumns<DspCreativeStats>(),
    this.dspColumns.currencyColumn<DspCreativeStats>(),
    this.buildActionColumn<DspCreativeStats>(
      (data) => this.translocoService.translate("dsp-stats.creative") + " - " + data.dspCreativeName,
      this.translocoService.translate("dsp-strategy-stats-grid.display_creative_stats_chart"),
      "creativeDetails",
    ),
  ]);

  readonly inventoryGridOptions = computed(() => this.gridOptions<DspInventoryStats>(this.inventoryGridKey()));
  readonly inventoryGridColumnDefs = computed<ColDef<DspInventoryStats>[]>(() => [
    {
      headerName: this.translocoService.translate("dsp-stats.site"),
      headerValueGetter: () => this.translocoService.translate("dsp-stats.site"),
      field: "site",
      floatingFilter: true,
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: this.translocoService.translate("dsp-stats.supply_source"),
      headerValueGetter: () => this.translocoService.translate("dsp-stats.supply_source"),
      field: "supplySource",
      pinned: "left",
      floatingFilter: true,
      filter: "agSetColumnFilter",
      enableRowGroup: true,
    },
    ...this.metricsColumns<DspInventoryStats>(),
    this.dspColumns.currencyColumn<DspInventoryStats>(),
    this.buildActionColumn<DspInventoryStats>(
      (data) => this.translocoService.translate("dsp-stats.site") + " - " + data.site,
      this.translocoService.translate("dsp-strategy-stats-grid.display_stats_chart"),
      "None",
    ),
  ]);

  private readonly availableAmcAnalyses$ = toObservable(this.strategy).pipe(
    switchMap((strategy) => this.amcStrategyAnalysisService.getDspStrategy(strategy.strategyId)),
  );
  availableAmcAnalyses = toSignal(this.availableAmcAnalyses$);

  restoreDefaultColumns() {
    switch (this.activeTab()) {
      case DspStrategyStatsSection.LINE_ITEMS:
        restoreDefaultColumns(this.lineItemGridKey(), this.lineItemGrid().api);
        break;
      case DspStrategyStatsSection.CREATIVES:
        restoreDefaultColumns(this.creativeGridKey(), this.creativeGrid().api);
        break;
      case DspStrategyStatsSection.INVENTORY:
        restoreDefaultColumns(this.inventoryGridKey(), this.inventoryGrid().api);
        break;
    }
  }

  exportGridCsv() {
    switch (this.activeTab()) {
      case DspStrategyStatsSection.LINE_ITEMS:
        this.dspColumns.exportCsv(this.lineItemGrid().api, {
          fileName: "dsp_line_item_stats",
          columnHeaders: {
            name: "dsp-stats.line_item",
            state: "dsp-stats.state",
            deliveryStatus: "dsp-stats.deliveryStatus",
          },
        });
        break;
      case DspStrategyStatsSection.CREATIVES:
        this.dspColumns.exportCsv(this.creativeGrid().api, {
          fileName: "dsp_creative_stats",
          columnHeaders: { dspCreativeName: "dsp-stats.creative", type: "dsp-stats.creativeType" },
        });
        break;
      case DspStrategyStatsSection.INVENTORY:
        this.dspColumns.exportCsv(this.inventoryGrid().api, {
          fileName: "dsp_inventory_stats",
          columnHeaders: { site: "dsp-stats.site", supplySource: "dsp-stats.supply_source" },
        });
        break;
    }
  }

  private filterStats(stats: DspStats[] | undefined): DspStats[] {
    if (!stats) {
      return [];
    }
    const strategy = this.strategy();
    return stats.filter((s) => s.strategyId === strategy.strategyId && !isEmptyDspStats(s));
  }

  private buildRowData<
    T extends DspStats & {
      stats?: DspStats[];
      previousData?: DspStats[];
      previousTotalData?: DspStats;
    },
  >(
    stats: DspStats[],
    previousStatsMap: Map<string, { stats: DspStats[]; total: DspStats }>,
    keyFn: (stat: DspStats) => string,
    init: (stat: DspStats) => T,
  ): T[] {
    if (!stats || stats.length === 0) {
      return [];
    }
    const strategyStats: Map<string, T> = new Map();
    for (const stat of stats) {
      const key = keyFn(stat);
      if (!strategyStats.has(key)) {
        strategyStats.set(key, {
          ...init(stat),
          stats: [stat],
          previousData: previousStatsMap.get(key)?.stats,
          previousTotalData: previousStatsMap.get(key)?.total,
        });
      } else {
        const existing = strategyStats.get(key);
        strategyStats.set(key, { ...existing!, ...sumDspStats(existing!, stat), stats: [...existing!.stats!, stat] });
      }
    }
    return Array.from(strategyStats.values()).sort((a, b) => (b.totalSales ?? 0) - (a.totalSales ?? 0));
  }

  private aggregatePreviousData(stats: DspStats[] | undefined, key: (s: DspStats) => string | undefined) {
    const res = new Map<string, { total: DspStats; stats: DspStats[] }>();
    if (!stats) {
      return res;
    }
    for (const stat of stats) {
      if (isEmptyDspStats(stat)) {
        continue;
      }
      const k = key(stat);
      if (!k) {
        continue;
      }
      if (!res.has(k)) {
        res.set(k, { total: { ...stat }, stats: [stat] });
      } else {
        const existing = res.get(k)!;
        existing.total = sumDspStats(existing.total, stat);
        existing.stats.push(stat);
      }
    }
    return res;
  }

  private gridOptions<T extends DspStats>(gridKey: string): GridOptions<T> {
    return dspGridOptions<T>({ gridKey, autoSizeDefault: true, withRowGrouping: true });
  }

  private metricsColumns<T extends DspStats & { previousTotalData?: DspStats }>(): ColDef<T>[] {
    const { metrics, defaultMetrics } = this.strategyMetrics();
    return this.dspColumns.metricsColumns<T>((params) => params.data?.previousTotalData, metrics, defaultMetrics);
  }

  private buildActionColumn<
    T extends DspStats & { stats?: DspStats[]; previousData?: DspStats[]; previousTotalData?: DspStats },
  >(
    titleFn: (data: T) => string,
    lineChartButtonTooltip: string,
    detailsButton: "None" | "lineItemDetails" | "creativeDetails",
  ): ColDef<T, string> {
    return {
      ...actionColumnProperties<T, string>(),
      cellRendererSelector: (params: ICellRendererParams<T, string>) => {
        if (params.node.isRowPinned() || params.data === undefined) {
          return undefined;
        }
        const title = titleFn(params.data);
        const { metrics, chartedMetrics, storageKeySuffix } = this.strategyMetrics();
        const btns: ActionButton[] = [
          {
            icon: "chartLine",
            tooltip: lineChartButtonTooltip,
            onClick: (params: ICellRendererParams<T, string>) => {
              const modalOption: ModalOptions<ChartModalInputData<DspStats>> = {
                modalTitle: title,
                data: {
                  locale: this.locale(),
                  currency: this.currency(),
                  dataSetConfiguration: {
                    aggregationFunction: [sumDspStats],
                    metricsOnSameScale: [[DSP_SALES, DSP_COST]],
                  },
                  chartData$: of({
                    data: params.data!.stats ?? [],
                    totalData: params.data!,
                    previousData: params.data?.previousData,
                    totalPreviousData: params.data?.previousTotalData,
                  }),
                  selectedMetrics: chartedMetrics,
                  metrics,
                  localStorageKey: {
                    page: MetricsSelectorPage.dspStrategyStats,
                    dspMetrics: storageKeySuffix,
                  },
                },
              };
              this.modalService.openModal<ChartModalInputData<DspStats>, void>(ChartModalComponent, modalOption);
            },
          },
        ];
        if (detailsButton === "lineItemDetails") {
          btns.push({
            icon: "list",
            tooltip: this.translocoService.translate("dsp-strategy-stats-grid.display_line_item_details"),
            onClick: (params: ICellRendererParams<DspLineItemStats, string>) => {
              const creativeData = params.data!.stats ?? [];
              this.modalService.openModal<DspStatsCreativeDetailsModalData, void>(
                DspStatsCreativeDetailsModalComponent,
                {
                  size: "xl",
                  modalTitle: title,
                  data: {
                    creativeData,
                    creatives: new Map(this.dspCreatives()?.map((c) => [c.adCreativeId!, c])),
                  },
                },
              );
            },
          });
        }
        if (detailsButton === "creativeDetails") {
          btns.push({
            icon: "list",
            tooltip: this.translocoService.translate("dsp-strategy-stats-grid.display_creative_stats_details"),
            onClick: (params: ICellRendererParams<DspCreativeStats, string>) => {
              const lineItemsData = params.data!.lineItemStats ?? [];
              this.modalService.openModal<DspStatsLineItemDetailsModalData, void>(
                DspStatsLineItemDetailsModalComponent,
                {
                  modalTitle: title,
                  data: {
                    lineItemsData,
                    lineItems: new Map(this.dspLineItems()?.map((l) => [l.adGroupId!, l])),
                  },
                },
              );
            },
          });
        }
        return {
          component: ActionButtonsComponent,
          params: {
            actionButtons: btns,
          },
        };
      },
    };
  }
}
